import { DEFAULT_LANG, PREFIXED_LANGS, isSupportedLang, type Lang } from '../data/languages.config';

// English is at the root, every other language under its own code: `/fr/catalog/pricing`.
// Config paths stay language-independent; a literal path in a template uses the `localePath` pipe.

/** Splits a href into the part a language prefix applies to, and the query/fragment after it. */
function splitPathname(href: string): { pathname: string; rest: string } {
  const at = href.search(/[?#]/);

  return at === -1
    ? { pathname: href, rest: '' }
    : { pathname: href.slice(0, at), rest: href.slice(at) };
}

/** `/fr/catalog` → `/catalog`; a path already without a prefix is returned as it is. */
function withoutLang(pathname: string): string {
  for (const code of PREFIXED_LANGS) {
    if (pathname === `/${code}`) return '/';
    if (pathname.startsWith(`/${code}/`)) return pathname.slice(code.length + 1);
  }

  return pathname;
}

/** The language `url` is addressed in; no prefix, or anything unparseable, is the default. */
export function langOfPath(url: string): Lang {
  const segment = splitPathname(url).pathname.split('/')[1];

  return isSupportedLang(segment) && segment !== DEFAULT_LANG ? segment : DEFAULT_LANG;
}

/** The path without its language prefix, which is what the routes and the sitemap are written in. */
export function canonicalPath(url: string): string {
  const { pathname, rest } = splitPathname(url);

  return withoutLang(pathname) + rest;
}

/** The address of `href` in `lang`; anything that is not a site-relative path is returned as it is. */
export function localePath(href: string, lang: Lang): string {
  if (!href.startsWith('/')) return href;

  const { pathname, rest } = splitPathname(href);
  const base = withoutLang(pathname);
  if (lang === DEFAULT_LANG) return base + rest;

  return (base === '/' ? `/${lang}` : `/${lang}${base}`) + rest;
}
